import React, { useState } from 'react';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import { Box, Link } from '@mui/material';
import { useAuth } from '@/contexts/AuthContext';
import { Formik, Form } from 'formik';
import * as yup from 'yup';
import type { LoginCredentials } from '@/types/auth';
import { styled } from '@mui/material/styles';
import { Button, Typography, Input } from '@/components/ui';
import { height } from '@mui/system';

// Styled components matching Regions design
const LoginContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  width: '100%',
  height: '100%',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
  },
}));

const LeftPanel = styled(Box)(({ theme }) => ({
  flex: '0 0 40%',
  padding: theme.spacing(2, 4),
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  backgroundColor: 'white',
  [theme.breakpoints.down('md')]: {
    flex: '1',
    padding: theme.spacing(4, 3),
  },
}));

const RightPanel = styled(Box)(({ theme }) => ({
  flex: '0 0 50%',
  position: 'relative',
  overflow: 'hidden',
  [theme.breakpoints.down('md')]: {
    flex: '0 0 200px',
  },
}));

// Login Banner styled components
const BannerContainer = styled(Box)(() => ({
  width: '100%',
  height: '100%',
  position: 'relative',
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)',
}));

const BannerImage = styled('img')(() => ({
  width: '100%',
  height: '100%',

  objectPosition: 'cover',
}));

const BrandHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(4),
  gap: theme.spacing(1.5),
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(2),
    textAlign: 'center',
  },
}));

const WelcomeSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
}));

const FormSection = styled(Box)(() => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
}));

const FooterLinks = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(3),
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: theme.spacing(2),
}));

const TeamInfo = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.primary.light,
  padding: theme.spacing(2),
  borderRadius: theme.spacing(1),
  marginBottom: theme.spacing(3),
  textAlign: 'center',
}));

const LogoHeader = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(3),
  left: theme.spacing(4),
  // [theme.breakpoints.down('sm')]: {
  //   left: '50%',
  //   transform: 'translateX(-50%)',
  // },
}));

const BRAND_COLOR = '#17A6DA';

const FormCard = styled(Box)(({ theme }) => ({
  background: '#ffffff',
  padding: theme.spacing(6, 4),
  borderRadius: '12px',
  boxShadow: `
  0 8px 16px rgba(0,0,0,0.08),
  0 20px 40px rgba(0,0,0,0.15)
`,
  border: '1px solid #f0f0f0',
}));

const inputSx = {
  '& .MuiInputBase-root': {
    backgroundColor: 'transparent !important',
  },

  '& .MuiOutlinedInput-root': {
    backgroundColor: 'transparent !important',
    borderRadius: '8px',

    '& fieldset': {
      borderColor: '#d0d5dd',
    },

    '&:hover fieldset': {
      borderColor: '#808080',
    },

    '&.Mui-focused fieldset': {
      borderColor: ' #808080',
      borderWidth: '2px',
    },
    height:'50px',
  },

  // Remove browser autofill bg
  '& input:-webkit-autofill': {
    WebkitBoxShadow: '0 0 0 1000px transparent inset',
    WebkitTextFillColor: '#000',
    transition: 'background-color 9999s ease-in-out 0s',
  },

  // Label colors
  '& .MuiInputLabel-root': {
    color: '#667085',
  },

  '& .MuiInputLabel-root.Mui-focused': {
    color: BRAND_COLOR,
  },
};

// Validation schema
const teamLoginSchema = yup.object({
  email: yup.string().required('Email ID is required').email('Please enter a valid email address'),
  password: yup
    .string()
    .required('Password is required')
    .min(6, 'Password must be at least 6 characters'),
});

// Initial form values
const initialValues: LoginCredentials = {
  email: '',
  password: '',
};

export const TeamLoginForm: React.FC = () => {
  const navigate = useNavigate();
  const { teamId } = useParams<{ teamId: string }>();
  const { teamLogin, loading } = useAuth();

  const [showPassword, setShowPassword] = useState(false);

  // Handle form submission
  const handleSubmit = async (values: LoginCredentials, { setFieldError }: { setFieldError: (field: string, message: string) => void }) => {
    if (!teamId) {
      setFieldError('email', 'Team ID is missing from URL');
      return;
    }

    try {
      await teamLogin(teamId, values);
      navigate('/dashboard', { replace: true });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Team login failed';
      setFieldError('email', errorMessage);
    }
  };

  return (
    <LoginContainer>
      <LogoHeader>
        <img
          src="/logos/logo-white_1.png"
          alt="GrowthBnk Logo"
          style={{ width: '150px', objectFit: 'contain' }}
        />
      </LogoHeader>
      {/* Left Panel - Login Form */}
      <LeftPanel>
        <FormSection>
          {/* Brand Header - Regions Style */}

          {/* Welcome Section - Team Login Style */}
          <WelcomeSection>
            <Typography
              variant="h1"
              weight="bold"
              color="textPrimary"
              textAlign={'center'}
              sx={{ fontSize: { xs: '1.00rem', sm: '1.5rem' }, color: 'rgb(40, 42, 48)' }}
            >
              Team Login
            </Typography>
            <Typography
              variant="body2"
              color="textSecondary"
              textAlign={'center'}
              sx={{ fontSize: { xs: '0.75rem', sm: '0.75rem' } }}
            >
              Sign in to your team account.
            </Typography>
          </WelcomeSection>

          {/* Team Info */}
          {/* {teamId && (
            <TeamInfo>
              <Typography variant="body2" color="primary.contrastText">
                Team ID: {teamId}
              </Typography>
            </TeamInfo>
          )} */}

          {/* Login Form */}
          <Box
            sx={{
              width: {
                xs: '320px', // Mobile devices
                sm: '420px', // Tablets
                md: '700px', // Desktops
                lg: '70%', // Large screens: full width
              },
              maxWidth: '100%',
              margin: '0 auto',
            }}
          >
            <FormCard>
              <Formik
                initialValues={initialValues}
                validationSchema={teamLoginSchema}
                onSubmit={handleSubmit}
              >
                {({ values, errors, touched, setFieldValue, setFieldTouched }) => (
                  <Form>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      {/* Email ID Field */}
                      <Box>
                        <Input
                          fullWidth
                          label="Email ID"
                          name="email"
                          type="text"
                          placeholder="Enter Email ID"
                          value={values.email}
                          onChange={e => setFieldValue('email', e.target.value)}
                          onBlur={() => setFieldTouched('email', true)}
                          onFocus={() => {
                            // Mark as touched when user focuses on the field
                            if (!touched.email) {
                              setFieldTouched('email', true);
                            }
                          }}
                          error={touched.email && !!errors.email}
                          helperText={touched.email && errors.email}
                          disabled={loading}
                          size="small"
                          variant="outlined"
                          sx={inputSx}
                        />
                      </Box>

                      {/* Password Field */}
                      <Box>
                        <Input
                          fullWidth
                          label="Password"
                          name="password"
                          type="password"
                          placeholder="Enter Password"
                          value={values.password}
                          onChange={e => setFieldValue('password', e.target.value)}
                          onBlur={() => setFieldTouched('password', true)}
                          onFocus={() => {
                            // Mark as touched when user focuses on the field
                            if (!touched.password) {
                              setFieldTouched('password', true);
                            }
                          }}
                          error={touched.password && !!errors.password}
                          helperText={touched.password && errors.password}
                          disabled={loading}
                          size="small"
                          variant="outlined"
                          showPassword={showPassword}
                          onPasswordToggle={() => setShowPassword(!showPassword)}
                          sx={inputSx}
                          
                        />
                      </Box>

                      {/* Login Button */}
                      <Button
                        type="submit"
                        variant="primary"
                        size="small"
                        fullWidth
                        loading={loading}
                        loadingText="Team SignIn in..."
                        disabled={!values.email || !values.password || !teamId}
                      >
                        Team SignIn
                      </Button>
                    </Box>
                  </Form>
                )}
              </Formik>
            </FormCard>
          </Box>

          {/* Footer Links */}
          <FooterLinks>
          </FooterLinks>
        </FormSection>
      </LeftPanel>

      {/* Right Panel - Login Banner */}
      <RightPanel>
        <BannerContainer>
          <BannerImage src="/banner.svg" alt="Team Login Banner" />
        </BannerContainer>
      </RightPanel>
    </LoginContainer>
  );
};

export default TeamLoginForm;
